import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '/api';
const apiFetch = async (url, opts) => {
  const r = await fetch(url, opts);
  if (!r.ok) {
    let msg;
    try { msg = await r.text(); } catch (e) { msg = r.statusText; }
    throw new Error(`HTTP ${r.status}: ${msg}`);
  }
  return r;
};

const Button = ({ children, color = 'emerald', onClick, small, outline, className = '' }) => (
  <button onClick={onClick}
    className={`${small ? 'px-2.5 py-1 text-[10px]' : 'px-3.5 py-1.5 text-[11px]'} font-semibold rounded-xl transition-all ${
      outline
        ? `border border-${color}-200 dark:border-${color}-800 text-${color}-600 dark:text-${color}-400 hover:bg-${color}-50 dark:hover:bg-${color}-900/10`
        : `bg-${color}-500 hover:bg-${color}-600 text-white shadow-lg shadow-${color}-500/20`
    } ${className}`}>
    {children}
  </button>
);

const Input = (p) => <input {...p} className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />;
const Label = ({ children }) => <label className="flex flex-col gap-1 text-[11px] font-medium text-surface-400">{children}</label>;

const GroupForm = ({ group, onSave, onClose }) => {
  const [form, setForm] = useState(group || { ad: '', sira: 0, zorunlu: true, minSecim: 0, maxSecim: 0, aktif: true });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white dark:bg-surface-800 rounded-3xl w-full max-w-md p-5 shadow-soft-xl animate-scale-in" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-surface-800 dark:text-white text-sm mb-4">{group ? 'Grubu Düzenle' : 'Yeni Grup'}</h3>
        <div className="space-y-3">
          <Label>Grup Adı<Input value={form.ad} onChange={e => set('ad', e.target.value)} required placeholder="Örn: Sunum Tipi" /></Label>
          <Label>Sıra<Input type="number" value={form.sira} onChange={e => set('sira', parseInt(e.target.value) || 0)} /></Label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-[11px] font-medium text-surface-400">
              <input type="checkbox" checked={form.zorunlu} onChange={e => set('zorunlu', e.target.checked)}
                className="w-4 h-4 rounded border-surface-300 dark:border-surface-600 text-emerald-500 focus:ring-emerald-500/20" />
              Zorunlu
            </label>
            <label className="flex items-center gap-2 text-[11px] font-medium text-surface-400">
              <input type="checkbox" checked={form.aktif !== false} onChange={e => set('aktif', e.target.checked)}
                className="w-4 h-4 rounded border-surface-300 dark:border-surface-600 text-emerald-500 focus:ring-emerald-500/20" />
              Aktif
            </label>
          </div>
          <div className="flex gap-3">
            <Label>Min Seçim<Input type="number" value={form.minSecim} onChange={e => set('minSecim', parseInt(e.target.value) || 0)} /></Label>
            <Label>Max Seçim<Input type="number" value={form.maxSecim} onChange={e => set('maxSecim', parseInt(e.target.value) || 0)} /></Label>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 text-xs font-semibold bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 rounded-xl hover:bg-surface-200 dark:hover:bg-surface-600 transition-all">İptal</button>
            <button onClick={() => onSave(form)} className="flex-1 py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all">Kaydet</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ItemForm = ({ item, onSave, onClose }) => {
  const [form, setForm] = useState(item || { ad: '', ekFiyat: null, sira: 0, aktif: true });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white dark:bg-surface-800 rounded-3xl w-full max-w-md p-5 shadow-soft-xl animate-scale-in" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-surface-800 dark:text-white text-sm mb-4">{item ? 'Seçeneği Düzenle' : 'Yeni Seçenek'}</h3>
        <div className="space-y-3">
          <Label>Seçenek Adı<Input value={form.ad} onChange={e => set('ad', e.target.value)} required placeholder="Örn: Külah" /></Label>
          <Label>Ek Fiyat (TL)<Input type="number" step="0.01" value={form.ekFiyat ?? ''} onChange={e => set('ekFiyat', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Boş/0 = ücretsiz" /></Label>
          <Label>Sıra<Input type="number" value={form.sira} onChange={e => set('sira', parseInt(e.target.value) || 0)} /></Label>
          <label className="flex items-center gap-2 text-[11px] font-medium text-surface-400">
            <input type="checkbox" checked={form.aktif !== false} onChange={e => set('aktif', e.target.checked)}
              className="w-4 h-4 rounded border-surface-300 dark:border-surface-600 text-emerald-500 focus:ring-emerald-500/20" />
            Aktif
          </label>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 text-xs font-semibold bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 rounded-xl hover:bg-surface-200 dark:hover:bg-surface-600 transition-all">İptal</button>
            <button onClick={() => onSave(form)} className="flex-1 py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all">Kaydet</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductMapping = ({ kategoriler, urunler, groups, onUrunGuncelle }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [seciliUrun, setSeciliUrun] = useState(null);
  const [urunMappings, setUrunMappings] = useState([]);
  const [modifierFlags, setModifierFlags] = useState({});
  const [seciliIds, setSeciliIds] = useState([]);
  const [topluSearch, setTopluSearch] = useState('');
  const [tekilSearch, setTekilSearch] = useState('');

  useEffect(() => {
    const flags = {};
    urunler.forEach(u => { flags[u.urunID] = u.hasModifiers || false; });
    setModifierFlags(flags);
  }, [urunler]);

  useEffect(() => {
    if (!seciliUrun) { setUrunMappings([]); return; }
    apiFetch(`${API}/productmodifiers/urun/${seciliUrun}/mappings`, { headers })
      .then(r => r.json()).then(setUrunMappings).catch(() => {});
  }, [seciliUrun, headers]);

  const toggleMapping = async (groupId) => {
    const existing = urunMappings.find(m => m.modifierGroupId === groupId);
    try {
      if (existing) {
        await apiFetch(`${API}/productmodifiers/${existing.id}`, { method: 'DELETE', headers });
      } else {
        await apiFetch(`${API}/productmodifiers`, {
          method: 'POST', headers,
          body: JSON.stringify({ urunId: seciliUrun, modifierGroupId: groupId })
        });
      }
      const r = await apiFetch(`${API}/productmodifiers/urun/${seciliUrun}/mappings`, { headers });
      setUrunMappings(await r.json());
    } catch (e) { Swal.fire('Hata', 'İşlem başarısız', 'error'); }
  };

  const toggleModifierFlag = async (urunId, value) => {
    const urun = urunler.find(u => u.urunID === urunId);
    if (!urun) return;
    try {
      await apiFetch(`${API}/urunler/${urunId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ ...urun, hasModifiers: value })
      });
      setModifierFlags(prev => ({ ...prev, [urunId]: value }));
      if (onUrunGuncelle) onUrunGuncelle();
    } catch (ex) {
      Swal.fire('Hata', 'Güncellenemedi', 'error');
    }
  };

  const topluGuncelle = async (value) => {
    if (seciliIds.length === 0) { Swal.fire('Uyarı', 'Lütfen en az bir ürün seçin', 'warning'); return; }
    try {
      await Promise.all(seciliIds.map(id => {
        const urun = urunler.find(u => u.urunID === id);
        if (!urun) return Promise.resolve();
        return apiFetch(`${API}/urunler/${id}`, {
          method: 'PUT', headers,
          body: JSON.stringify({ ...urun, hasModifiers: value })
        });
      }));
      setModifierFlags(prev => {
        const next = { ...prev };
        seciliIds.forEach(id => { next[id] = value; });
        return next;
      });
      setSeciliIds([]);
      if (onUrunGuncelle) onUrunGuncelle();
      Swal.fire({ icon: 'success', title: `${seciliIds.length} ürün güncellendi`, timer: 1500, showConfirmButton: false });
    } catch (ex) { Swal.fire('Hata', 'Toplu güncelleme başarısız', 'error'); }
  };

  const toggleSecim = (id) => {
    setSeciliIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  return (
    <div className="space-y-4">
      {/* Toplu işlem */}
      <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft p-4">
        <h3 className="font-bold text-surface-800 dark:text-white text-sm mb-3">Toplu Düzenle</h3>
        <p className="text-[10px] text-surface-400 mb-3">Ürünleri işaretleyin, ardından toplu olarak modifier kullanır/aç yapın</p>
        <input value={topluSearch} onChange={e => setTopluSearch(e.target.value)} placeholder="Ürün ara..."
          className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 mb-2" />
        <div className="max-h-48 overflow-y-auto custom-scrollbar mb-3 space-y-0.5">
          {urunler.filter(u => u.aktif !== false && (!topluSearch || u.urunAdi.toLowerCase().includes(topluSearch.toLowerCase()))).map(u => (
            <label key={u.urunID}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-colors text-[11px] ${
                seciliIds.includes(u.urunID) ? 'bg-emerald-50 dark:bg-emerald-900/10' : 'hover:bg-surface-50 dark:hover:bg-surface-700/30'
              }`}>
              <input type="checkbox" checked={seciliIds.includes(u.urunID)} onChange={() => toggleSecim(u.urunID)}
                className="w-3.5 h-3.5 rounded border-surface-300 dark:border-surface-600 text-emerald-500 focus:ring-emerald-500/20" />
              <span className="flex-1 text-surface-700 dark:text-surface-300">{u.urunAdi}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                modifierFlags[u.urunID] ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-surface-100 text-surface-400 dark:bg-surface-700 dark:text-surface-500'
              }`}>{modifierFlags[u.urunID] ? '✨ Aktif' : 'Normal'}</span>
            </label>
          ))}
        </div>
        {seciliIds.length > 0 && (
          <div className="flex gap-2">
            <button onClick={() => topluGuncelle(true)}
              className="flex-1 py-2 text-[11px] font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all">
              ✨ Modifier Aç ({seciliIds.length})
            </button>
            <button onClick={() => topluGuncelle(false)}
              className="flex-1 py-2 text-[11px] font-semibold bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 rounded-xl hover:bg-surface-200 dark:hover:bg-surface-600 transition-all">
              Modifier Kapat
            </button>
          </div>
        )}
      </div>

      {/* Tekil düzenleme */}
      <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft p-4">
        <h3 className="font-bold text-surface-800 dark:text-white text-sm mb-3">Tekil Düzenleme</h3>
        <div className="mb-3 flex flex-col gap-2">
          <Label>Ürün Ara
            <input value={tekilSearch} onChange={e => setTekilSearch(e.target.value)} placeholder="Ürün adı yazın..."
              className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
          </Label>
          <Label>Ürün Seçin
            <select value={seciliUrun || ''} onChange={e => setSeciliUrun(parseInt(e.target.value))}
              className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
              <option value="">Seçiniz</option>
              {kategoriler.map(k => {
                const filtrelenmis = urunler.filter(u => u.kategoriID === k.kategoriID && (!tekilSearch || u.urunAdi.toLowerCase().includes(tekilSearch.toLowerCase())));
                if (filtrelenmis.length === 0) return null;
                return (
                  <optgroup key={k.kategoriID} label={k.kategoriAdi}>
                    {filtrelenmis.map(u => (
                      <option key={u.urunID} value={u.urunID}>{u.urunAdi} {modifierFlags[u.urunID] ? '✨' : ''}</option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </Label>
        </div>

        {seciliUrun && (
          <>
            <label className="flex items-center gap-2 text-[11px] font-medium text-surface-400 mb-4">
              <input type="checkbox" checked={modifierFlags[seciliUrun] || false}
                onChange={async (e) => { await toggleModifierFlag(seciliUrun, e.target.checked); }}
                className="w-4 h-4 rounded border-surface-300 dark:border-surface-600 text-emerald-500 focus:ring-emerald-500/20" />
              Bu ürün modifier kullanır ✨
            </label>

            <div className="space-y-1.5 max-h-60 overflow-y-auto custom-scrollbar">
              {groups.filter(g => g.aktif !== false).map(g => {
                const mapped = urunMappings.some(m => m.modifierGroupId === g.id);
                return (
                  <div key={g.id} className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-surface-50 dark:hover:bg-surface-700/30 transition-colors">
                    <div>
                      <span className="text-xs font-medium text-surface-700 dark:text-surface-300">{g.ad}</span>
                      <span className="text-[10px] text-surface-400 ml-2">({g.minSecim}-{g.maxSecim || '∞'})</span>
                    </div>
                    <button onClick={() => toggleMapping(g.id)}
                      className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg transition-all ${mapped ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'bg-surface-100 dark:bg-surface-700 text-surface-400 dark:text-surface-500 hover:bg-surface-200 dark:hover:bg-surface-600'}`}>
                      {mapped ? 'Eşleşti' : 'Eşleşmedi'}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const QuickCombos = ({ urunler, groups, onAdd, onEdit, refreshKey }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [combos, setCombos] = useState([]);

  const fetchCombos = async () => {
    try {
      const r = await apiFetch(`${API}/hizlikombinasyon`, { headers });
      setCombos(await r.json());
    } catch (e) {}
  };

  useEffect(() => { fetchCombos(); }, [refreshKey]);

  const deleteCombo = async (id) => {
    const r = await Swal.fire({ title: 'Emin misiniz?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Sil', cancelButtonText: 'İptal' });
    if (!r.isConfirmed) return;
    try { await apiFetch(`${API}/hizlikombinasyon/${id}`, { method: 'DELETE', headers }); fetchCombos(); }
    catch (e) { Swal.fire('Hata', 'Silinemedi', 'error'); }
  };

  return (
    <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft">
      <div className="flex items-center justify-between p-4 border-b border-surface-100 dark:border-surface-700">
        <h3 className="font-bold text-surface-800 dark:text-white text-sm">Hızlı Kombinasyonlar</h3>
        <button onClick={() => onAdd()}
          className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all">+ Yeni</button>
      </div>
      <div className="divide-y divide-surface-100 dark:divide-surface-700/50">
        {combos.map(c => {
          const secimler = JSON.parse(c.modifierSecimleri || '[]');
          return (
            <div key={c.id} className="flex items-center justify-between p-3.5">
              <div>
                <p className="text-xs font-semibold text-surface-700 dark:text-surface-300">{c.ad}</p>
                <p className="text-[10px] text-surface-400">{c.urunAdi} • {secimler.length} seçim</p>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => onEdit({ ...c, urunId: c.urunId })}
                  className="px-2 py-1 text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all">Düzenle</button>
                <button onClick={() => deleteCombo(c.id)}
                  className="px-2 py-1 text-[10px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-all">Sil</button>
              </div>
            </div>
          );
        })}
        {combos.length === 0 && <p className="p-4 text-xs text-surface-400 text-center">Henüz kombinasyon eklenmemiş</p>}
      </div>
    </div>
  );
};

const ComboForm = ({ combos, urunler, groups, onSave, onClose }) => {
  const [form, setForm] = useState(combos || {});
  const [itemSecim, setItemSecim] = useState([]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (combos?.modifierSecimleri) {
      try { setItemSecim(JSON.parse(combos.modifierSecimleri)); } catch (e) {}
    }
  }, [combos]);

  const toggleItem = (itemId) => {
    setItemSecim(prev => {
      const exists = prev.find(s => s.modifierItemId === itemId);
      if (exists) return prev.filter(s => s.modifierItemId !== itemId);
      return [...prev, { modifierItemId: itemId }];
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white dark:bg-surface-800 rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5 shadow-soft-xl animate-scale-in" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-surface-800 dark:text-white text-sm mb-4">{combos.id ? 'Kombinasyon Düzenle' : 'Yeni Kombinasyon'}</h3>
        <div className="space-y-3">
          <Label>Kombinasyon Adı<Input value={form.ad || ''} onChange={e => set('ad', e.target.value)} required placeholder="Örn: Karışık Special" /></Label>
          <Label>Ürün
            <select value={form.urunId || ''} onChange={e => set('urunId', parseInt(e.target.value))}
              className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
              <option value="">Seçiniz</option>
              {urunler.filter(u => u.hasModifiers).map(u => (
                <option key={u.urunID} value={u.urunID}>{u.urunAdi}</option>
              ))}
            </select>
          </Label>
          <Label>Sıra<Input type="number" value={form.sira || 0} onChange={e => set('sira', parseInt(e.target.value) || 0)} /></Label>

          {form.urunId && (
            <div>
              <p className="text-[11px] font-medium text-surface-400 mb-2">Modifier Seçimleri</p>
              <div className="space-y-3">
                {groups.filter(g => g.aktif !== false).map(g => (
                  <div key={g.id} className="bg-surface-50 dark:bg-surface-700/30 rounded-xl p-3">
                    <p className="text-[10px] font-semibold text-surface-500 mb-2">{g.ad}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {g.items?.filter(i => i.aktif !== false).map(i => {
                        const secili = itemSecim.some(s => s.modifierItemId === i.id);
                        return (
                          <button key={i.id} onClick={() => toggleItem(i.id)}
                            className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg transition-all ${secili ? 'bg-emerald-500 text-white shadow-sm' : 'bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 text-surface-500 dark:text-surface-400 hover:border-emerald-300'}`}>
                            {i.ad}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 text-xs font-semibold bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 rounded-xl hover:bg-surface-200 dark:hover:bg-surface-600 transition-all">İptal</button>
            <button onClick={() => onSave({ ...form, modifierSecimleri: itemSecim })}
              className="flex-1 py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all">Kaydet</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModifierYonetimi = ({ kategoriler, urunler, onUrunGuncelle }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [groups, setGroups] = useState([]);
  const [groupForm, setGroupForm] = useState(null);
  const [itemForm, setItemForm] = useState(null);
  const [itemFormGroupId, setItemFormGroupId] = useState(null);
  const [comboForm, setComboForm] = useState(null);
  const [comboRefreshKey, setComboRefreshKey] = useState(0);
  const [tab, setTab] = useState('groups');

  const fetchGroups = async () => {
    try {
      const r = await apiFetch(`${API}/modifiergroups`, { headers });
      setGroups(await r.json());
    } catch (e) { console.warn('fetchGroups error:', e); }
  };

  useEffect(() => { fetchGroups(); }, []);

  const saveGroup = async (form) => {
    try {
      if (form.id) {
        await apiFetch(`${API}/modifiergroups/${form.id}`, { method: 'PUT', headers, body: JSON.stringify(form) });
      } else {
        await apiFetch(`${API}/modifiergroups`, { method: 'POST', headers, body: JSON.stringify(form) });
      }
      setGroupForm(null);
      fetchGroups();
      Swal.fire({ icon: 'success', title: 'Kaydedildi', timer: 1000, showConfirmButton: false });
    } catch (e) { Swal.fire('Hata', 'Kaydedilemedi', 'error'); }
  };

  const deleteGroup = async (id) => {
    const r = await Swal.fire({ title: 'Emin misiniz?', text: 'Grup pasif hale getirilecek', icon: 'warning', showCancelButton: true, confirmButtonText: 'Evet', cancelButtonText: 'İptal' });
    if (!r.isConfirmed) return;
    try { await apiFetch(`${API}/modifiergroups/${id}`, { method: 'DELETE', headers }); fetchGroups(); }
    catch (e) { Swal.fire('Hata', 'Silinemedi', 'error'); }
  };

  const saveItem = async (form) => {
    try {
      if (form.id) {
        await apiFetch(`${API}/modifiergroups/items/${form.id}`, { method: 'PUT', headers, body: JSON.stringify(form) });
      } else {
        await apiFetch(`${API}/modifiergroups/${itemFormGroupId}/items`, { method: 'POST', headers, body: JSON.stringify(form) });
      }
      setItemForm(null);
      fetchGroups();
      Swal.fire({ icon: 'success', title: 'Kaydedildi', timer: 1000, showConfirmButton: false });
    } catch (e) { Swal.fire('Hata', 'Kaydedilemedi', 'error'); }
  };

  const deleteItem = async (id) => {
    const r = await Swal.fire({ title: 'Emin misiniz?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Evet', cancelButtonText: 'İptal' });
    if (!r.isConfirmed) return;
    try { await apiFetch(`${API}/modifiergroups/items/${id}`, { method: 'DELETE', headers }); fetchGroups(); }
    catch (e) { Swal.fire('Hata', 'Silinemedi', 'error'); }
  };

  const saveCombo = async (form) => {
    try {
      const body = {
        ad: form.ad, urunId: form.urunId,
        modifierSecimleri: JSON.stringify(form.modifierSecimleri || []),
        sira: form.sira || 0, aktif: true
      };
      if (form.id) {
        await apiFetch(`${API}/hizlikombinasyon/${form.id}`, { method: 'PUT', headers, body: JSON.stringify(body) });
      } else {
        await apiFetch(`${API}/hizlikombinasyon`, { method: 'POST', headers, body: JSON.stringify(body) });
      }
      setComboForm(null);
      setComboRefreshKey(k => k + 1);
      Swal.fire({ icon: 'success', title: 'Kaydedildi', timer: 1000, showConfirmButton: false });
    } catch (e) { Swal.fire('Hata', 'Kaydedilemedi', 'error'); }
  };

  const tabs = [
    { key: 'groups', label: 'Gruplar ve Seçenekler' },
    { key: 'mapping', label: 'Ürün Eşleme' },
    { key: 'combos', label: 'Hızlı Kombinasyonlar' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-1.5 overflow-x-auto pb-2">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3.5 py-1.5 text-[11px] font-semibold rounded-xl whitespace-nowrap transition-all ${
              tab === t.key ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'groups' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-surface-800 dark:text-white text-sm">Modifier Grupları</h3>
            <button onClick={() => setGroupForm({})}
              className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all">+ Grup Ekle</button>
          </div>
          {groups.length === 0 ? (
            <p className="text-xs text-surface-400 text-center py-8">Henüz modifier grubu eklenmemiş. Dondurma için 'Sunum Tipi', 'Top Sayısı', 'Aroma' gibi gruplar oluşturun.</p>
          ) : (
            <div className="space-y-3">
              {groups.map(g => (
                <div key={g.id} className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft overflow-hidden">
                  <div className="flex items-center justify-between p-4 bg-surface-50 dark:bg-surface-700/30 border-b border-surface-100 dark:border-surface-700">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-lg bg-surface-200 dark:bg-surface-600 flex items-center justify-center text-[9px] font-bold text-surface-500 dark:text-surface-400">{g.sira || 0}</span>
                      <h4 className="font-semibold text-surface-800 dark:text-white text-sm">{g.ad}</h4>
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${g.zorunlu ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-surface-200 text-surface-500 dark:bg-surface-600 dark:text-surface-400'}`}>{g.zorunlu ? 'Zorunlu' : 'Opsiyonel'}</span>
                      <span className="text-[10px] text-surface-400">{g.minSecim}-{g.maxSecim || '∞'} seçim</span>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => { setItemFormGroupId(g.id); setItemForm({}); }}
                        className="px-2 py-1 text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-all">+ Seçenek</button>
                      <button onClick={() => setGroupForm(g)}
                        className="px-2 py-1 text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all">Düzenle</button>
                      <button onClick={() => deleteGroup(g.id)}
                        className="px-2 py-1 text-[10px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-all">Sil</button>
                    </div>
                  </div>
                  {g.items && g.items.length > 0 ? (
                    <div className="divide-y divide-surface-100 dark:divide-surface-700/50">
                      {g.items.map(i => (
                        <div key={i.id} className="flex items-center justify-between px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-surface-700 dark:text-surface-300">{i.ad}</span>
                            {i.ekFiyat > 0 && <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">+{i.ekFiyat} TL</span>}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => { setItemFormGroupId(g.id); setItemForm(i); }}
                              className="px-2 py-0.5 text-[9px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all">Düzenle</button>
                            <button onClick={() => deleteItem(i.id)}
                              className="px-2 py-0.5 text-[9px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-all">Sil</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="p-4 text-[11px] text-surface-400 text-center">Henüz seçenek eklenmemiş</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'mapping' && <ProductMapping kategoriler={kategoriler} urunler={urunler} groups={groups} onUrunGuncelle={onUrunGuncelle} />}
      {tab === 'combos' && <QuickCombos urunler={urunler} groups={groups} onAdd={() => setComboForm({})} onEdit={(c) => setComboForm(c)} refreshKey={comboRefreshKey} />}

      {groupForm && <GroupForm group={groupForm.id ? groupForm : null} onSave={saveGroup} onClose={() => setGroupForm(null)} />}
      {itemForm && <ItemForm item={itemForm.id ? itemForm : null} onSave={saveItem} onClose={() => setItemForm(null)} />}
      {comboForm && <ComboForm combos={comboForm} urunler={urunler} groups={groups} onSave={saveCombo} onClose={() => setComboForm(null)} />}
    </div>
  );
};

export default ModifierYonetimi;
